/* eslint-disable max-len */
import { enc } from 'crypto-js';

export const isValidEmailExpression = /(^$|^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$)/;

export const isThereHtmlTags = /^[^</>]*$/;

export const hasLowerChar = /[a-z]+/;
export const hasUpperChar = /[A-Z]+/;
export const hasNumberChar = /[0-9]+/;
export const hasSpecialChar = /[~!@#$%^&*()\-_+{}|[\]\\:;"'?,./]+/;
export const allowedChars = /^[0-9A-Za-z~!@#$%^&*()\-_+{}|[\]\\:;"'?,./]+$/;
export const passwordLength = /^.{8,30}$/;
export const email = /(^$|^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$)/;

export const getFormattedToken = (token: string) => {
  let formattedToken = '';

  if (token?.startsWith('Bearer ')) {
    formattedToken = token.substring('Bearer '.length);
  }
  return formattedToken;
};

export const strongPassword = (password: any) => {
  if (!passwordLength.test(password)) return false;
  if (!allowedChars.test(password)) return false;

  const checks = [
    hasLowerChar.test(password),
    hasUpperChar.test(password),
    hasNumberChar.test(password),
    hasSpecialChar.test(password),
  ];

  if (checks.filter(Boolean).length < 3) return false;
  return true;
};

export const getEncodedUserId = (jwt: string) => {
  const base64Url = jwt.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = enc.Base64.parse(base64).toString(enc.Utf8);
  const jsonParsed = JSON.parse(jsonPayload);
  return jsonParsed.user_id;
};

export const getBrowserLocale = (): string => {
  const locale = navigator.languages && navigator.languages.length ? navigator.languages[0] : navigator.language;
  return locale;
};

export const formatAssessmentDate = (assessmentDate: any) => {
  if (!assessmentDate) return '';

  const userLocale = 'en-US';
  const receivedDate = new Date(assessmentDate);

  if (isNaN(receivedDate.getTime())) {
    return assessmentDate;
  }

  // Check if the date string indicates UTC (ends with Z) or has timezone info
  const dateStr = assessmentDate.toString();
  const isUTC = dateStr.endsWith('Z');
  const hasTimezone = dateStr.includes('+') || dateStr.includes('Z') || dateStr.match(/[+-]\d{2}:\d{2}$/);

  // If it's UTC (Z) or has timezone info, use UTC formatting to preserve the intended date
  if (isUTC || hasTimezone) {
    return receivedDate.toLocaleDateString(userLocale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone: 'UTC',
    });
  }

  // Otherwise, use local timezone (for dates without timezone info)
  return receivedDate.toLocaleDateString(userLocale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

export const trimAndLowCase = (text: string) => (text.trim().toLowerCase());

export const downloadReport = async (report: string) => {
  try {
    if (report) {
      // Decode HTML entities (e.g., &amp; to &)
      const decodedUrl = report.replace(/&amp;/g, '&');

      // Extract filename from URL
      const url = new URL(decodedUrl);
      const pathname = url.pathname;
      const isZip = pathname.includes('.zip') || decodedUrl.includes('.zip');
      const isPdf = pathname.includes('.pdf') || decodedUrl.includes('.pdf');

      let fileName = 'report';
      if (isZip) {
        fileName = pathname.split('/').pop()?.split('?')[0] || 'batch_report.zip';
      } else if (isPdf) {
        const pathParts = pathname.split('/');
        fileName = pathParts[pathParts.length - 1] || 'report.pdf';
      }

      // Use direct anchor download to bypass CORS restrictions
      const a = document.createElement('a');
      a.href = decodedUrl;
      a.download = fileName;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';

      // Append to body, click, and remove
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  } catch (err) {
    console.error(err);
  }
};

export const printReport = (report: string) => {
  try {
    window.open(report, '_blank');
  } catch (err) {
    console.error(err);
  }
};

export const getProductsNames = (productMap: { [key: string]: { name: string } }): { id: string; label: string; }[] => {
  const products: { id: string; label: string; }[] = [];
  Object.keys(productMap).forEach((productId: string) => {
    products.push({ id: productId, label: productMap[productId].name });
  });
  return products;
};

export const getStatusLabels = (statusMap: { [key: string]: string }): { id: string; label: string; }[] => {
  const status: { id: string; label: string; }[] = [];
  Object.keys(statusMap).forEach((statusId: string) => {
    status.push({ id: statusId, label: statusMap[statusId] });
  });
  return status;
};

export const getBase64ImageExtension = (image: string): string | null => {
  const match = image.match(/data:image\/([^;]+)/);
  return match ? match[1] : null;
};
